import React, { useState, useEffect, useCallback } from 'react';
import Loader from '../../components/common/Loader';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES } from '../../utils/constants';
import { formatDate } from '../../utils/helpers';
import axiosInstance from '../../api/axios';

// ─── Reverse Confirmation Modal ───────────────────────────────────────────────
const ReverseModal = ({ log, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
      <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
        <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">Reverse Manual Vote</h3>
      <p className="text-sm text-gray-600 text-center mb-4">
        This will remove the manually added votes and recalculate the contestant's vote count.
      </p>
      <div className="bg-gray-50 rounded-lg p-4 mb-4 grid grid-cols-2 gap-2 text-sm">
        <span className="text-gray-500">Contestant:</span>
        <span className="font-semibold text-gray-900">{log?.contestant?.name}</span>
        <span className="text-gray-500">Votes to remove:</span>
        <span className="font-semibold text-red-600">-{log?.voteCount}</span>
        <span className="text-gray-500">Added by:</span>
        <span className="font-semibold text-gray-900">
          {log?.addedByUser?.firstName} {log?.addedByUser?.lastName}
        </span>
        <span className="text-gray-500">Added on:</span>
        <span className="font-semibold text-gray-900">{formatDate(log?.createdAt)}</span>
      </div>
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-5 text-xs text-red-800">
        <strong>This cannot be undone.</strong> The contestant's vote count will be corrected automatically.
      </div>
      <div className="flex space-x-3">
        <button
          onClick={onCancel}
          disabled={loading}
          className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center"
        >
          {loading
            ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Reversing…</>
            : 'Yes, Reverse'}
        </button>
      </div>
    </div>
  </div>
);

// ─── Add Manual Vote Modal ────────────────────────────────────────────────────
const AddManualVoteModal = ({ contestId, onSuccess, onCancel }) => {
  const [contestants, setContestants] = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [selectedCategory,   setSelectedCategory]   = useState('');
  const [selectedContestant, setSelectedContestant] = useState('');
  const [voteCount, setVoteCount] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const { error: showError } = useToast();

  useEffect(() => {
    // Fetch categories for the contest
    // axiosInstance.get(`/contests/${contestId}/categories`)
    axiosInstance.get(`/categories/contest/${contestId}`)
      // .then(r => setCategories(r.data?.data || []))
      .then(r => setCategories(r.data?.data || r.data?.categories || []))
      .catch(() => {});
  }, [contestId]);

  useEffect(() => {
    if (!selectedCategory) { setContestants([]); return; }
    axiosInstance.get(`/contestants/category/${selectedCategory}`)
      .then(r => setContestants(r.data?.data || []))
      .catch(() => {});
  }, [selectedCategory]);

  const handleSubmit = async () => {
    if (!selectedCategory || !selectedContestant || voteCount < 1) {
      showError('Please fill in all fields');
      return;
    }
    try {
      setSubmitting(true);
      await axiosInstance.post('/votes/manual', {
        contestId,
        categoryId:   selectedCategory,
        contestantId: selectedContestant,
        voteCount:    parseInt(voteCount),
      });
      onSuccess();
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to add manual vote');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Manual Votes</h3>

        <div className="space-y-4">
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={selectedCategory}
              onChange={e => { setSelectedCategory(e.target.value); setSelectedContestant(''); }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select category…</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* Contestant */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contestant</label>
            <select
              value={selectedContestant}
              onChange={e => setSelectedContestant(e.target.value)}
              disabled={!selectedCategory}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">Select contestant…</option>
              {contestants.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* Vote count */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Number of Votes</label>
            <input
              type="number"
              min="1"
              value={voteCount}
              onChange={e => setVoteCount(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4 text-xs text-yellow-800">
          <strong>Note:</strong> Manual votes are logged with your name and timestamp for audit purposes.
          They can be reversed if added by mistake.
        </div>

        <div className="flex space-x-3 mt-5">
          <button
            onClick={onCancel}
            disabled={submitting}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !selectedCategory || !selectedContestant}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center"
          >
            {submitting
              ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Adding…</>
              : 'Add Votes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Tab Component ───────────────────────────────────────────────────────
const ManualVotesTab = ({ selectedContest, contests }) => {
  const { error: showError, success: showSuccess } = useToast();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === USER_ROLES.SUPER_ADMIN;

  const [logs,       setLogs]       = useState([]);
  const [summary,    setSummary]    = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });

  // Filters
  const [filterReversed,  setFilterReversed]  = useState('');   // '' | 'false' | 'true'
  const [filterContestId, setFilterContestId] = useState('');   // Super Admin only
  const [searchTerm,      setSearchTerm]      = useState('');

  // Modals
  const [showReverseModal,  setShowReverseModal]  = useState(false);
  const [showAddModal,      setShowAddModal]      = useState(false);
  const [selectedLog,       setSelectedLog]       = useState(null);
  const [reversing,         setReversing]         = useState(false);

  const effectiveContestId = isSuperAdmin ? (filterContestId || selectedContest) : selectedContest;

  const fetchLogs = useCallback(async (page = 1) => {
    if (!effectiveContestId && !isSuperAdmin) return;
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page,
        pageSize: 50,
        ...(filterReversed !== '' && { isReversed: filterReversed }),
        ...(isSuperAdmin && filterContestId && { contestId: filterContestId }),
      });

      const url = isSuperAdmin && !effectiveContestId
        ? `/votes/manual-logs?${params}`
        : `/votes/manual-logs/contest/${effectiveContestId}?${params}`;

      const res = await axiosInstance.get(url);
      if (res.data?.success) {
        setLogs(res.data.data?.logs || []);
        setPagination(res.data.data?.pagination || { page: 1, totalPages: 1, total: 0 });
        setSummary(res.data.data?.summary || null);
      }
    } catch (err) {
      showError('Failed to load manual vote logs');
    } finally {
      setLoading(false);
    }
  }, [effectiveContestId, filterReversed, filterContestId, isSuperAdmin]);

  useEffect(() => { fetchLogs(1); }, [effectiveContestId, filterReversed, filterContestId]);

  const handleReverse = async () => {
    if (!selectedLog) return;
    try {
      setReversing(true);
      await axiosInstance.post(`/votes/manual-logs/${selectedLog.id}/reverse`);
      showSuccess(`Reversed ${selectedLog.voteCount} manual vote(s) for ${selectedLog.contestant?.name}`);
      setShowReverseModal(false);
      setSelectedLog(null);
      fetchLogs(pagination.page);
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to reverse manual vote');
    } finally {
      setReversing(false);
    }
  };

  // Client-side search filter
  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      log.contestant?.name?.toLowerCase().includes(q) ||
      log.contest?.title?.toLowerCase().includes(q) ||
      log.category?.name?.toLowerCase().includes(q) ||
      `${log.addedByUser?.firstName} ${log.addedByUser?.lastName}`.toLowerCase().includes(q)
    );
  });

  const selectedContestData = contests?.find(c => c.id === selectedContest);

  return (
    <div>
      {/* Modals */}
      {showReverseModal && selectedLog && (
        <ReverseModal
          log={selectedLog}
          onConfirm={handleReverse}
          onCancel={() => { setShowReverseModal(false); setSelectedLog(null); }}
          loading={reversing}
        />
      )}
      {showAddModal && (
        <AddManualVoteModal
          contestId={effectiveContestId}
          onSuccess={() => {
            showSuccess('Manual votes added successfully');
            setShowAddModal(false);
            fetchLogs(1);
          }}
          onCancel={() => setShowAddModal(false)}
        />
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Total Manual Votes',    value: summary.totalManualVotes,    color: 'blue',   desc: 'All time' },
            { label: 'Active Manual Votes',   value: summary.activeManualVotes,   color: 'green',  desc: 'Currently counted' },
            { label: 'Reversed Manual Votes', value: summary.reversedManualVotes, color: 'red',    desc: 'Removed from count' },
          ].map(({ label, value, color, desc }) => (
            <div key={label} className={`bg-${color}-50 border border-${color}-200 rounded-lg p-4`}>
              <p className={`text-sm font-medium text-${color}-700`}>{label}</p>
              <p className={`text-3xl font-bold text-${color}-600 mt-1`}>{value?.toLocaleString()}</p>
              <p className={`text-xs text-${color}-500 mt-1`}>{desc}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters + Add Button */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <div className="flex flex-col md:flex-row md:items-end gap-3">

          {/* Super Admin contest filter */}
          {isSuperAdmin && (
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Contest</label>
              <select
                value={filterContestId}
                onChange={e => setFilterContestId(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All contests</option>
                {contests?.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
          )}

          {/* Status filter */}
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select
              value={filterReversed}
              onChange={e => setFilterReversed(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="false">Active</option>
              <option value="true">Reversed</option>
            </select>
          </div>

          {/* Search */}
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
            <input
              type="text"
              placeholder="Contestant, admin, category…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Add button */}
          {effectiveContestId && (
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center whitespace-nowrap"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Add Manual Votes
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Manual Vote Log</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {filteredLogs.length} entr{filteredLogs.length !== 1 ? 'ies' : 'y'} shown
              {selectedContestData && !isSuperAdmin && ` — ${selectedContestData.title}`}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader size="md" text="Loading logs…" />
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-16">
            <svg className="mx-auto h-10 w-10 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="text-sm text-gray-500">No manual vote logs found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    'Contestant', 'Category',
                    ...(isSuperAdmin ? ['Contest'] : []),
                    'Votes Added', 'Added By', 'Added On', 'Status', 'Reversed By', 'Actions'
                  ].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLogs.map(log => (
                  <tr key={log.id} className={`hover:bg-gray-50 transition-colors ${log.isReversed ? 'opacity-60' : ''}`}>
                    {/* Contestant */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        {log.contestant?.photo ? (
                          <img src={log.contestant.photo} alt="" className="w-7 h-7 rounded-full object-cover mr-2" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center mr-2">
                            <span className="text-xs text-gray-500">{log.contestant?.name?.[0]}</span>
                          </div>
                        )}
                        <span className="text-sm font-medium text-gray-900">{log.contestant?.name}</span>
                      </div>
                    </td>

                    {/* Category */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {log.category?.name}
                    </td>

                    {/* Contest (Super Admin only) */}
                    {isSuperAdmin && (
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {log.contest?.title}
                      </td>
                    )}

                    {/* Votes */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`text-sm font-bold ${log.isReversed ? 'line-through text-gray-400' : 'text-blue-600'}`}>
                        +{log.voteCount?.toLocaleString()}
                      </span>
                    </td>

                    {/* Added by */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {log.addedByUser?.firstName} {log.addedByUser?.lastName}
                      </div>
                      <div className="text-xs text-gray-500">{log.addedByUser?.email}</div>
                    </td>

                    {/* Added on */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(log.createdAt)}
                    </td>

                    {/* Status badge */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {log.isReversed ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                          Reversed
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                          Active
                        </span>
                      )}
                    </td>

                    {/* Reversed by */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {log.isReversed && log.reversedByUser ? (
                        <div>
                          <div className="text-sm text-gray-900">
                            {log.reversedByUser.firstName} {log.reversedByUser.lastName}
                          </div>
                          <div className="text-xs text-gray-500">{formatDate(log.reversedAt)}</div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      {!log.isReversed ? (
                        <button
                          onClick={() => { setSelectedLog(log); setShowReverseModal(true); }}
                          className="inline-flex items-center px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 text-xs font-medium rounded-md hover:bg-red-100 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                              d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                          </svg>
                          Reverse
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Reversed</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => fetchLogs(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-3 py-1.5 border border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => fetchLogs(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="px-3 py-1.5 border border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualVotesTab;